import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Star, ChevronLeft, ChevronRight } from 'lucide-react';

const PricingSection = ({ onOpenPopup }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const plans = [
    {
      name: "Essentials",
      price: "$45",
      period: "+ Federal Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Basic Search",
        "State E-Filing",
        "Trademark Secured"
      ],
      note: "Govt. filing fee not included",
      popular: false
    },
    {
      name: "Enhanced",
      price: "$149",
      period: "+ Federal Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter"
      ],
      note: "Govt. filing fee not included",
      popular: false
    },
    {
      name: "Deluxe",
      price: "$299",
      period: "+ Federal Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager"
      ],
      note: "Govt. filing fee not included",
      popular: true,
      badge: "Recommended"
    },
    {
      name: "Platinum",
      price: "$349",
      period: "+ Federal Filing Fee",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager",
        "High Approval Rate",
        "Comprehensive Search Report",
        "High Priority Service",
        "Cease & Desist Letter"
      ],
      note: "Govt. filing fee not included",
      popular: false
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % plans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + plans.length) % plans.length);
  };

  return (
    <section id="pricing" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <h2 className="section-title text-center">Transparent Pricing Plans</h2>
        <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16">
          Choose the trademark registration package that best fits your needs. All plans include 
          expert guidance and support throughout the registration process.
        </p>

        {/* Desktop View */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <Card 
              key={index} 
              className={`relative p-6 flex flex-col ${
                plan.popular 
                  ? 'border-2 border-secondary shadow-xl scale-105' 
                  : 'border border-gray-200 card-hover'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-secondary text-primary px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <Star className="w-4 h-4" fill="currentColor" />
                    {plan.badge}
                  </span>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-primary mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-primary mb-1">{plan.price}</div>
                <div className="text-sm text-gray-600">{plan.period}</div>
              </div>

              <div className="flex-1 overflow-y-auto max-h-80 mb-6 scrollbar-thin scrollbar-thumb-secondary scrollbar-track-gray-100">
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto">
                <p className="text-xs text-gray-500 text-center mb-4 italic">{plan.note}</p>
                <Button 
                  onClick={onOpenPopup}
                  className={`w-full ${
                    plan.popular ? 'btn-primary' : 'btn-outline'
                  }`}
                >
                  {plan.popular ? 'Get Started Now' : 'Choose Plan'}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Mobile Slider View */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {plans.map((plan, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <Card 
                    className={`relative p-6 ${
                      plan.popular 
                        ? 'border-2 border-secondary shadow-xl' 
                        : 'border border-gray-200'
                    }`}
                  >
                    {plan.badge && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="bg-secondary text-primary px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                          <Star className="w-4 h-4" fill="currentColor" />
                          {plan.badge}
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-primary mb-2">{plan.name}</h3>
                      <div className="text-4xl font-bold text-primary mb-1">{plan.price}</div>
                      <div className="text-sm text-gray-600">{plan.period}</div>
                    </div>

                    <div className="overflow-y-auto max-h-80 mb-6 scrollbar-thin scrollbar-thumb-secondary scrollbar-track-gray-100">
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 text-center mb-4 italic">{plan.note}</p>
                      <Button 
                        onClick={onOpenPopup}
                        className={`w-full ${
                          plan.popular ? 'btn-primary' : 'btn-outline'
                        }`}
                      >
                        {plan.popular ? 'Get Started Now' : 'Choose Plan'}
                      </Button>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={prevSlide}
              className="p-2 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
              aria-label="Previous plan"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <div className="flex gap-2">
              {plans.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === index 
                      ? 'bg-secondary w-8' 
                      : 'bg-gray-300'
                  }`}
                  aria-label={`Go to plan ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="p-2 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
              aria-label="Next plan"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            All prices are subject to applicable government filing fees
          </p>
          <Button onClick={onOpenPopup} variant="outline" className="btn-outline">
            Need Help Choosing? Contact Us
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
